import {
  Input,
  Form,
  Select,
  DatePicker,
  FormInstance,
  TreeSelect,
} from 'antd'
import type { Dayjs } from 'dayjs'
import React from 'react'

import TableSearch from '@/components/shared/table-search'
import {
  NormalStatus,
  normalStatusOptions,
} from '@/constants/normal-status'
import { useDayjsFromEvent } from '@/hooks/use-date-time-form-item'
import { GroupType } from '@/model/group'
import { selectAccessibleCreators } from '@/redux/creator-selector'
import { selectAccessibleEmployees } from '@/redux/employee-selector'
import { selectFeatureTimezone } from '@/redux/global-selector'
import { selectGroupTreeData } from '@/redux/group-selectors'
import {
  createAppSelector,
  useAppSelector,
} from '@/redux/utils'
import { dayjs } from '@/utils/dayjs'

const { Item } = Form

const selector = createAppSelector([
  selectAccessibleCreators,
  (state) => selectGroupTreeData(state, GroupType.Employee),
  selectAccessibleEmployees,
], (creators, groupOptions, employees) => ({
  groupOptions,
  creatorOptions: creators.map(({ id, name }) => ({
    value: id,
    label: name,
  })),
  employeeOptions: employees.map(({ id, name }) => ({
    value: id,
    label: name,
  })),
}),
)

export interface SearchEmployeeValues {
  name?: string;
  status?: NormalStatus;
  groupId?: number;
  employeeId?: number;
  creatorId?: number;
  date?: Dayjs;
}

export interface SearchEmployeeProps<Values> {
  onSearch: (values: Values | undefined, form: FormInstance<Values>) => Promise<void> | void
  searchItems?: Array<'groupId' | 'name' | 'employeeId' | 'creatorId' | 'status' | 'date'>
}

export default function SearchEmployee({
  onSearch,
  searchItems = ['name', 'creatorId', 'status'],
}: SearchEmployeeProps<SearchEmployeeValues>) {
  const { groupOptions, creatorOptions, employeeOptions } = useAppSelector(selector)
  const { featureTimezoneName } = useAppSelector(selectFeatureTimezone)
  const defaultDate = dayjs().tz(featureTimezoneName)
  const getDayjsFromEvent = useDayjsFromEvent()

  const tableSearchItems = [
    <Item key="groupId" name="groupId" label="Employee group" style={{ width: 320 }}>
      <TreeSelect<number>
        treeData={groupOptions}
        placeholder="Group"
        dropdownStyle={{ minWidth: 320, maxWidth: 400, overflow: 'auto' }}
        treeDefaultExpandAll
        allowClear
      />
    </Item>,
    <Item key="name" name="name" label="Employee">
      <Input placeholder="Employee" allowClear />
    </Item>,
    <Item key="employeeId" name="employeeId" label="Employee" style={{ width: 240 }}>
      <Select options={employeeOptions} placeholder="Employee" allowClear />
    </Item>,
    <Item key="creatorId" name="creatorId" label="Creator" style={{ width: 240 }}>
      <Select options={creatorOptions} placeholder="Creator" allowClear />
    </Item>,
    <Item key="status" name="status" label="Status" style={{ width: 200 }}>
      <Select options={normalStatusOptions} placeholder="Status" allowClear />
    </Item>,
    <Item
      key="date"
      name="date"
      label="Date"
      getValueFromEvent={getDayjsFromEvent}
      style={{ width: 320 }}
    >
      <DatePicker format="MMMM YYYY" picker="month" allowClear={false} />
    </Item>]

  return (
    <TableSearch<SearchEmployeeValues>
      onSearch={onSearch}
      initialValues={{ date: defaultDate }}
    >
      {tableSearchItems.filter(
        ({ key }) => searchItems.some((searchItem) => searchItem === key),
      )}
    </TableSearch>
  )
}
